export interface FormField {
  id: string;
  label?: string;
  type: 'text' | 'textarea';
  rows?: number;
  showRefreshButton: boolean;
}

interface SubCategory {
  id: string;
  title?: string;
  fields: FormField[];
}

interface Category {
  id: string;
  title?: string;
  subCategories: SubCategory[];
}

interface Section {
  id: string;
  title: string;
  categories: Category[];
}

export const formSections: Section[] = [
  {
    id: 'section1',
    title: 'Section 1: Company Overview',
    categories: [
      {
        id: 'company_info',
        subCategories: [
          {
            id: 'basic_info',
            title: 'Company Name',
            fields: [
              {
                id: 'company_name',
                type: 'text',
                showRefreshButton: false
              }
            ]
          }
        ]
      },
      {
        id: 'concept_summary',
        title: 'Concept Summary',
        subCategories: [
          {
            id: 'problem_solution',
            fields: [
              {
                id: 'problem_statement',
                label: 'What problem are you solving? For who? What is the result?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'industry',
                label: 'What is the industry or sector categorization?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'competitors',
                label: 'Is anyone else doing this? How are you different?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'company_details',
        title: 'Company Details',
        subCategories: [
          {
            id: 'details',
            fields: [
              {
                id: 'idea_source',
                label: 'Is this a founder-led idea, corporate initiative, or based on existing IP?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'progress',
                label: 'What have you accomplished to date (idea, concept, actively developing, scaling, etc.)?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'traction',
                label: 'Do you have any preliminary traction or purchase intent from existing or prospective customers?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'location',
                label: 'Are you Toronto-based or willing to move to Toronto to develop this idea?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'section2',
    title: 'Section 2: Business Proposal',
    categories: [
      {
        id: 'customer',
        title: 'Customer',
        subCategories: [
          {
            id: 'customer_details',
            fields: [
              {
                id: 'target_audience',
                label: 'Who has this problem? What is the market size of the solution?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'ideal_customer',
                label: 'Who is the ideal customer? Sector, size, problem, people, frequency.',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'non_ideal_customer',
                label: 'Who is not the ideal customer? What do you run away from?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'value_proposition',
        title: 'Value Proposition',
        subCategories: [
          {
            id: 'valueDetails',
            fields: [
              {
                id: 'quantifiable_value',
                label: 'Is there a quantifiable value proposition? What is it?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'problem_frequency',
                label: 'How often (frequency) does your user have the problem? How intense is the problem?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'success_metrics',
                label: 'How will they know if your product has solved the problem?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'feature_set',
        title: 'Feature Set',
        subCategories: [
          {
            id: 'featureDetails',
            fields: [
              {
                id: 'mvp',
                label: 'What is the MVP? Do you have a feature development roadmap?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'first_users',
                label: 'Who will use the first \'bad\' product? Are they paying customers, partners, free users?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'problem_fit',
                label: 'Does your product actually solve the problem? How and why not?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'business_model',
        title: 'Business Model',
        subCategories: [
          {
            id: 'businessModelDetails',
            fields: [
              {
                id: 'early_customers',
                label: 'Who is your early customer? How do you know them?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'pricing',
                label: 'What is your pricing model? How do you price your product?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'buyers',
                label: 'Who are your buyers? How do you know them?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'section3',
    title: 'Section 3: Partnership with AXL',
    categories: [
      {
        id: 'why_you',
        title: 'Why You?',
        subCategories: [
          {
            id: 'founder_details',
            fields: [
              {
                id: 'founder_fit',
                label: 'Why are you the best person to turn this idea into a billion-dollar company?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'role',
                label: 'What role do you see yourself playing at the company moving forward?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'team_growth',
                label: 'Who are your first 5 ideal hires?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'why_us',
        title: 'Why Us?',
        subCategories: [
          {
            id: 'partnershipDetails',
            fields: [
              {
                id: 'axl_support',
                label: 'What can AXL do to best support the development of your idea?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'tech_resources',
                label: 'How intensive do you believe your idea will be on AXL\'s technical team? Be as specific as possible.',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'business_support',
                label: 'Are you looking for support on the business development and market validation process?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      },
      {
        id: 'why_now',
        title: 'Why Now?',
        subCategories: [
          {
            id: 'timingDetails',
            fields: [
              {
                id: 'founder_timing',
                label: 'You have lots of options. Why are you the right person to do this now?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'market_timing',
                label: 'What has changed in the market to make this the right time to launch your solution?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              },
              {
                id: 'differentiation',
                label: 'How will this idea stay ahead or stay differentiated long enough to become the market leader?',
                type: 'textarea',
                rows: 4,
                showRefreshButton: true
              }
            ]
          }
        ]
      }
    ]
  }
];